/**
 * Brokered GitHub approval and setup-required rows (moved from forge's
 * MessagesTimeline.tsx, ticket 3525; behavior unchanged). Kept as dedicated
 * cards: promoted blocker entries render here instead of joining work groups.
 *
 * @module TimelineGitHubRows
 */

import { Button } from "../ui/button";
import { CircleAlertIcon } from "lucide-react";
import { useState } from "react";
import { use } from "react";
import {
  actionRequiredCtaLabel,
  buildGitHubActionRequiredRetryText,
  formatGitHubActionRequiredLabel,
  resolveGitHubActionRequiredDetail,
  sameGitHubActionRequired,
  type GitHubToolConfirmationRequest,
} from "../../session-logic";
import type { GitHubActionRequiredRequest } from "../../session-logic";
import { formatTimestamp } from "../../timestampFormat";
import { actionRequiredSettingsHref } from "./githubActionRequired";
import {
  buildGitHubConfirmationDetailRows,
  buildGitHubConfirmationRetryText,
  formatGitHubOperationForDisplay,
  getGitHubConfirmationRepoLabel,
  githubOperationRiskLabel,
} from "./githubConfirmationPresentation";
import {
  isExpiredGitHubConfirmationApprovalError,
  isGitHubReauthRequiredError,
} from "../ChatView.logic";

import { TimelineRowCtx } from "./TimelineRowContext";

type GitHubApprovalStatus =
  | "idle"
  | "approving"
  | "approved"
  | "declined"
  | "failed"
  | "expired"
  | "reauthRequired";

interface GitHubApprovalButtonView {
  readonly label: string;
  readonly disabled: boolean;
}

/**
 * Maps the approval card's local status to the Approve button's label + enabled
 * state. `expired` and `declined` are terminal: the confirmation cannot be
 * re-approved, so the button stays disabled.
 */
function deriveGitHubApprovalButtonView(status: GitHubApprovalStatus): GitHubApprovalButtonView {
  switch (status) {
    case "approving":
      return { label: "Approving...", disabled: true };
    case "approved":
      return { label: "Approved", disabled: true };
    case "expired":
      return { label: "Expired", disabled: true };
    case "reauthRequired":
      return { label: "Sign-in required", disabled: true };
    case "declined":
      return { label: "Declined", disabled: true };
    case "failed":
    case "idle":
      return { label: "Approve", disabled: false };
  }
}

export function GitHubActionRequiredRow(props: {
  actionRequired: GitHubActionRequiredRequest;
  createdAt: string;
}) {
  const { actionRequired, createdAt } = props;
  const [status, setStatus] = useState<"idle" | "retrying" | "queued" | "failed">("idle");
  const [error, setError] = useState<string | null>(null);
  const ctx = use(TimelineRowCtx);
  const settingsHref = actionRequiredSettingsHref(actionRequired.action);
  const detail = resolveGitHubActionRequiredDetail(actionRequired);
  const retryText = buildGitHubActionRequiredRetryText(actionRequired);

  const retry = async () => {
    setStatus("retrying");
    setError(null);
    try {
      await ctx.onRetryGitHubActionRequired(actionRequired);
      setStatus("queued");
    } catch (retryError) {
      setStatus("failed");
      setError(retryError instanceof Error ? retryError.message : "Retry failed.");
    }
  };

  // Suppressed while the composer-anchored panel owns this exact blocker, so the
  // ask never shows twice (with two Retry buttons). All hooks run above first.
  if (
    ctx.activeActionRequired &&
    sameGitHubActionRequired(ctx.activeActionRequired, actionRequired)
  ) {
    return null;
  }

  return (
    <div className="rounded-lg border border-warning-border bg-warning-surface px-3 py-2">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-warning-foreground">
          <CircleAlertIcon className="size-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-xs font-medium text-foreground/90">
              {formatGitHubActionRequiredLabel(actionRequired.action)}
            </p>
            {actionRequired.toolName ? (
              <span className="max-w-full truncate rounded border border-border/50 bg-background/45 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/80">
                {actionRequired.toolName}
              </span>
            ) : null}
          </div>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground/75">{detail}</p>
          {status === "queued" ? (
            <p className="mt-1 text-[11px] leading-4 text-success-foreground">
              Retry queued. Forge will continue the GitHub action in this thread.
            </p>
          ) : null}
          {status === "failed" && error ? (
            <p className="mt-1 text-[11px] leading-4 text-destructive-foreground">{error}</p>
          ) : null}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {settingsHref ? (
              <Button
                size="xs"
                variant="outline"
                render={<a href={settingsHref}>{actionRequiredCtaLabel(actionRequired.action)}</a>}
              />
            ) : null}
            <Button
              type="button"
              size="xs"
              variant={settingsHref ? "ghost" : "outline"}
              disabled={status === "retrying" || status === "queued"}
              onClick={retry}
            >
              {status === "retrying" ? "Retrying..." : status === "queued" ? "Queued" : "Retry now"}
            </Button>
            <Button
              type="button"
              size="xs"
              variant="ghost"
              onClick={() => navigator.clipboard?.writeText(retryText).catch(() => undefined)}
            >
              Copy retry
            </Button>
            <span className="text-[10px] text-muted-foreground/45">
              {formatTimestamp(createdAt, ctx.timestampFormat)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export function GitHubToolConfirmationRow(props: {
  confirmation: GitHubToolConfirmationRequest;
  createdAt: string;
}) {
  const { confirmation, createdAt } = props;
  const [localStatus, setLocalStatus] = useState<GitHubApprovalStatus>("idle");
  const [error, setError] = useState<string | null>(null);
  const [reauthHref, setReauthHref] = useState<string | null>(null);
  const ctx = use(TimelineRowCtx);
  const isSuppressed = ctx.suppressedGitHubConfirmationId === confirmation.confirmationId;
  const isDeclinedViaOverlay = ctx.declinedGitHubConfirmationId === confirmation.confirmationId;
  const status: GitHubApprovalStatus = isDeclinedViaOverlay ? "declined" : localStatus;
  const repoLabel = getGitHubConfirmationRepoLabel(confirmation);
  const retryText = buildGitHubConfirmationRetryText(confirmation);
  const detailRows = buildGitHubConfirmationDetailRows(confirmation);
  const riskLabel = githubOperationRiskLabel(confirmation.operation);
  const approvalButtonView = deriveGitHubApprovalButtonView(status);

  const approve = async () => {
    setLocalStatus("approving");
    setError(null);
    try {
      await ctx.onApproveGitHubConfirmation(confirmation);
      setLocalStatus("approved");
    } catch (approvalError) {
      if (isExpiredGitHubConfirmationApprovalError(approvalError)) {
        setLocalStatus("expired");
        setError(null);
        return;
      }
      if (isGitHubReauthRequiredError(approvalError)) {
        setReauthHref(approvalError.reauthHref);
        setLocalStatus("reauthRequired");
        setError(null);
        return;
      }
      setLocalStatus("failed");
      setError(approvalError instanceof Error ? approvalError.message : "Approval failed.");
    }
  };

  return (
    <div className="rounded-lg border border-warning-border bg-warning-surface px-3 py-2">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center text-warning-foreground">
          <CircleAlertIcon className="size-3.5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <p className="text-xs font-medium text-foreground/90">
              {formatGitHubOperationForDisplay(confirmation.operation)}
            </p>
            <span className="max-w-full truncate rounded border border-border/50 bg-background/45 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/80">
              {repoLabel}
            </span>
          </div>
          <p className="mt-1 text-[11px] leading-4 text-muted-foreground/75">
            Forge will not run this GitHub write action until you approve it.
          </p>
          <dl className="mt-2 grid gap-1 rounded-md border border-border/45 bg-background/35 p-2 text-[11px] leading-4 sm:grid-cols-2">
            <div className="min-w-0">
              <dt className="text-[10px] font-medium uppercase text-muted-foreground/50">Risk</dt>
              <dd className="truncate text-warning-foreground">{riskLabel}</dd>
            </div>
            {detailRows.map((row) => (
              <div key={row.label} className="min-w-0">
                <dt className="text-[10px] font-medium uppercase text-muted-foreground/50">
                  {row.label}
                </dt>
                <dd className="truncate font-mono text-muted-foreground/85">{row.value}</dd>
              </div>
            ))}
          </dl>
          {status === "approved" ? (
            <p className="mt-1 text-[11px] leading-4 text-success-foreground">
              Approved. Forge ran the GitHub action or queued the retry.
            </p>
          ) : null}
          {status === "expired" ? (
            <p className="mt-1 text-[11px] leading-4 text-muted-foreground/70">
              This approval expired because its agent session was replaced. Re-run the action to get
              a fresh approval.
            </p>
          ) : null}
          {status === "reauthRequired" ? (
            <div className="mt-1 space-y-1.5">
              <p className="text-[11px] leading-4 text-warning-foreground">
                Your CMMD session expired, so Forge can&apos;t run this GitHub action. Sign in to
                CMMD, then reconnect this cloud project and retry.
              </p>
              {reauthHref ? (
                <Button
                  size="xs"
                  variant="outline"
                  render={<a href={reauthHref}>Sign in to CMMD</a>}
                />
              ) : null}
            </div>
          ) : null}
          {status === "declined" ? (
            <p className="mt-1 text-[11px] leading-4 text-muted-foreground/70">
              Declined. The agent was notified.
            </p>
          ) : null}
          {status === "failed" && error ? (
            <p className="mt-1 text-[11px] leading-4 text-destructive-foreground">{error}</p>
          ) : null}
          {isSuppressed ? (
            <p className="mt-2 text-[11px] leading-4 text-warning-foreground">
              ↓ Use the approval panel below to approve or decline.
            </p>
          ) : (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="xs"
                variant="outline"
                disabled={approvalButtonView.disabled}
                onClick={approve}
              >
                {approvalButtonView.label}
              </Button>
              <Button
                type="button"
                size="xs"
                variant="ghost"
                onClick={() => navigator.clipboard?.writeText(retryText).catch(() => undefined)}
              >
                Copy retry
              </Button>
              <span className="text-[10px] text-muted-foreground/45">
                {formatTimestamp(createdAt, ctx.timestampFormat)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
